// src/components/CtaGradient.tsx
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useRef } from 'react';

interface CtaGradientProps {
  title?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  className?: string;
}

const CtaGradient = ({
  title = 'Ready to Transform Your Digital Experience?',
  description = "Let's discuss how we can help you achieve your business goals.",
  buttonText = 'Get Started',
  buttonLink = '/contact',
  className,
}: CtaGradientProps) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    navigate(buttonLink);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.div
      ref={containerRef}
      className={cn(
        'rounded-3xl overflow-hidden shadow-xl ring-white ring-4 hover:shadow-2xl pointer-events-auto',
        className,
      )}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}>
      <div className="p-10 md:p-16 text-white relative overflow-hidden">
        {/* Base gradient layer - animated */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-[#12c2e9] via-[#c471ed] to-[#f64f59]"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          style={{
            backgroundSize: '200% 200%',
          }}
          transition={{
            duration: 15,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          }}
        />

        {/* Content container */}
        <div className="relative z-10">
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}>
            {title}
          </motion.h2>

          <motion.p
            className="text-xl md:text-2xl text-white/80 mb-8 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}>
            {description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}>
            <Button
              onClick={handleClick}
              className="bg-white text-gray-900 hover:bg-white/90 hover:text-black text-lg py-6 px-8 rounded-full font-bold"
              size="lg">
              {buttonText}
            </Button>
          </motion.div>
        </div>

        {/* Visual elements - animated decorative circles */}
        <motion.div
          className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#c471ed]/30 blur-xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
        <motion.div
          className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-[#12c2e9]/30 blur-xl"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
        />
      </div>
    </motion.div>
  );
};

export default CtaGradient;
